import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import ChartHeader from '../components/ChartHeader';
import ChartDetail from '../components/ChartDetail';
import CastSetting from '../components/CastSetting';
import ComboOptimizer from '../components/ComboOptimizer';
import ChartOverlay from '../components/ChartOverlay';
import { OptimizerResult, SkillActivation } from '../types/optimizer';
import { songIdFromChart } from '../optimizer/songSearch';
import type { SongSearchResult } from '../optimizer/songSearch';

function Chart() {
    
    const search = useLocation().search;
    const navigate = useNavigate();
    const chartID: string  = new URLSearchParams(search).get('id') ?? "m1_w1";
    const chartLevel = new URLSearchParams(search).get('level') ?? '1';
    const fullChartId = `${chartID}_${chartLevel}`.toLowerCase();
    const [songResults, setSongResults] = useState<SongSearchResult | null>(null);
    const songResult = songResults?.song_id === songIdFromChart(fullChartId) ? songResults : null;
    const [optimizerResult, setOptimizerResult] = useState<OptimizerResult | null>(null);
    const [selectedSkill, setSelectedSkill] = useState<SkillActivation | null>(null);
    const [comboOpen, setComboOpen] = useState(false);
    const result = songResult?.levels.find(level => level.chart_id === fullChartId)?.result
        ?? (optimizerResult?.chart_id === fullChartId ? optimizerResult : null);
    const handleResult = useCallback((next: OptimizerResult | null) => {
        setOptimizerResult(next);
        setSelectedSkill(null);
    }, []);
    const openLevel = useCallback((id: string) => {
        const song = songIdFromChart(id);
        const level = id.split('_').pop();
        navigate(`/Chart?id=${encodeURIComponent(song)}&level=${level}`);
    }, [navigate]);
    const handleSongResult = useCallback((next: SongSearchResult | null) => {
        setSongResults(next);
        setOptimizerResult(null);
        setSelectedSkill(null);
        if (next?.best) openLevel(next.best.chart_id);
    }, [openLevel]);
    const [chartColors, setChartColors] = useState(["rgb(59, 130, 246)","rgb(236, 72, 153)","rgb(16, 185, 129)","rgb(245, 158, 11)","rgb(139, 92, 246)"]);
    
    // State for Density Heatmap Window (default 10s)
    const [densityWindow, setDensityWindow] = useState(10); 
    
    // State for Max Duration
    const [songMaxDuration, setSongMaxDuration] = useState(180);

    const selectSkill = (skill: SkillActivation) => {
        setSelectedSkill(skill);
        setComboOpen(false);
    };

    return (
        <div className="h-[100dvh] flex flex-col bg-gray-50 overflow-hidden">
            <ChartHeader />
            
            <CastSetting 
                colors={chartColors} 
                setChartColors={setChartColors} 
                densityWindow={densityWindow} 
                setDensityWindow={setDensityWindow} 
                maxDuration={songMaxDuration}
                onOpenCombo={() => setComboOpen(true)}
            />
            
            <div className="flex-1 min-w-0 min-h-0 relative">
                <ChartDetail 
                    id={fullChartId} 
                    colors={chartColors} 
                    densityWindow={densityWindow} 
                    setMaxDuration={setSongMaxDuration}
                    activations={result?.best?.activations ?? []}
                    selectedSkill={selectedSkill}
                    onSelectSkill={selectSkill}
                />
            </div>
            <ChartOverlay open={comboOpen} title="Best combo" drawer onClose={() => setComboOpen(false)}>
                <ComboOptimizer chartId={fullChartId} result={result} onResult={handleResult}
                    onSelect={selectSkill} selected={selectedSkill} songResult={songResult}
                    onSongResult={handleSongResult} onOpenLevel={openLevel} showHeading={false} />
            </ChartOverlay>
        </div>
    );
}

export default Chart;
